import { useCallback } from 'react';

import { MedicalDevice, MedicalDeviceQuery, MedicalDeviceType } from '@dreryk/egabinet-ezwm-frontend-api';

import { CqrsQueryPerformResult, performCqrsQuerySearch } from '@dreryk/command-query-react-common';
import { paginations } from '@dreryk/command-query-ws-client';
import { Autosuggest, Text } from '@dreryk/react-components';
import { TextPattern } from '@dreryk/watermelon';

import { medicalDevicesTranslationKeys } from '../../translations';
import { MedicalDeviceOrderFormRegister, MedicalDeviceOrderFormWatch } from '../medicalDeviceOrder.types';

type MedicalDeviceSearchProps = {
   readonly register: MedicalDeviceOrderFormRegister;
   readonly watch: MedicalDeviceOrderFormWatch;
};

const useMedicalDeviceDeferredSearchCreator = (
   medicalDeviceType: MedicalDeviceType
): ((searchEntry: string | null) => () => CqrsQueryPerformResult<MedicalDevice[]>) => {
   return useCallback(
      (searchEntry) => {
         return async () =>
            performCqrsQuerySearch(() => {
               const query = new MedicalDeviceQuery();
               query.dictionaryType = 'NFZ';
               query.medicalDeviceType = medicalDeviceType;
               query.searchEntry = TextPattern.anywhereIgnoreCase(searchEntry ?? '');
               return query;
            }, paginations.all());
      },
      [medicalDeviceType]
   );
};

const MINIMUM_SEARCH_TRIGGER_CHARS = 3;

const medicalDeviceSearchItemRenderer = (item: MedicalDevice): JSX.Element => {
   return (
      <Text testId={'medicalDeviceSearchResult'}>
         {item.code} {item.name}
      </Text>
   );
};
const medicalDeviceSearchLabelExtractor = (item: MedicalDevice): string => {
   return `${item.code} ${item.name}`;
};

export const MedicalDeviceSearch = (props: MedicalDeviceSearchProps): JSX.Element => {
   const deviceType = props.watch('deviceType');
   return (
      <Autosuggest<MedicalDevice>
         testId={'medicalDeviceSearch'}
         label={medicalDevicesTranslationKeys.fields.medicalDevice}
         controller={props.register('device', { required: true })}
         searchCreator={useMedicalDeviceDeferredSearchCreator(deviceType)}
         itemRenderer={medicalDeviceSearchItemRenderer}
         labelExtractor={medicalDeviceSearchLabelExtractor}
         keyProperty={'id'}
         placeholder={medicalDevicesTranslationKeys.fields.fillCodeOrCriteriaName}
         minChars={MINIMUM_SEARCH_TRIGGER_CHARS}
      />
   );
};
